/** @format */

import { getDayE, getDayS } from './constants';
import { planningDayEndMin, planningDayStartMin } from './planningDefaultStart';
import { technicianIsJokerOnDate } from './technicianRoleSchedule';
import { isPastPlanningDay, m2t, t2m } from './time';

export const UNAVAILABILITY_KIND = 'unavailability';
export const AVAILABILITY_KIND = 'availability';

// First day inverted joker semantics apply (deploy date). Before: legacy.
export const JOKER_INVERT_AVAILABILITY_FROM = '2026-06-09';
export const JOKER_MIGRATED_AVAILABILITY_END = '2026-08-16';

function jokerUsesInvertedAvailabilityOnDate(dateStr) {
  return Boolean(dateStr && dateStr >= JOKER_INVERT_AVAILABILITY_FROM);
}

function effectiveInvertedJoker(isJoker, dateStr) {
  return Boolean(isJoker) && jokerUsesInvertedAvailabilityOnDate(dateStr);
}

/** @returns {string} HH:MM from API time ("08:00:00") or passthrough. */
export function planningTimeFromApi(value) {
  if (!value) return '';
  const text = String(value);
  return text.length >= 5 ? text.slice(0, 5) : text;
}

/** @returns {string} for API TimeField from "HH:MM" input. */
export function planningTimeToApi(value) {
  const text = (value || '').trim();
  if (!text) return '';
  return text.length === 5 ? `${text}:00` : text;
}

function timeToMin(value, fallbackMin) {
  if (!value) return fallbackMin;
  return t2m(value);
}

function recordKind(record) {
  return record?.kind || UNAVAILABILITY_KIND;
}

export function absencesForTechnicianOnDate(technicianId, dateStr, records) {
  if (!technicianId || !dateStr || !records?.length) return [];
  const id = Number(technicianId);
  return records.filter(
    (row) =>
      Number(row.technicianId) === id &&
      row.startDate <= dateStr &&
      row.endDate >= dateStr,
  );
}

/** Effective unavailable window on one calendar day (minutes from midnight). */
export function absenceRangeOnDate(record, dateStr) {
  if (!record || record.startDate > dateStr || record.endDate < dateStr) {
    return null;
  }

  if (record.wholeDay) {
    return {
      id: record.id,
      startMin: planningDayStartMin(dateStr),
      endMin: planningDayEndMin(dateStr),
      reason: record.reason || '',
      note: record.note || '',
    };
  }

  const dayStart = getDayS();
  const dayEnd = getDayE();
  const startMin =
    record.startDate === dateStr ? timeToMin(record.startTime, dayStart) : dayStart;
  const endMin =
    record.endDate === dateStr ? timeToMin(record.endTime, dayEnd) : dayEnd;

  const clampedStart = Math.max(startMin, dayStart);
  const clampedEnd = Math.min(endMin, dayEnd);
  if (clampedEnd <= clampedStart) return null;

  return {
    id: record.id,
    startMin: clampedStart,
    endMin: clampedEnd,
    reason: record.reason || '',
    note: record.note || '',
  };
}

function mergeMinuteRanges(ranges) {
  if (!ranges.length) return [];
  const sorted = [...ranges].sort((a, b) => a.startMin - b.startMin);
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    if (current.startMin <= last.endMin) {
      last.endMin = Math.max(last.endMin, current.endMin);
      if (!last.reason && current.reason) last.reason = current.reason;
      if (!last.note && current.note) last.note = current.note;
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}

export function availabilityRangesOnDate(technicianId, dateStr, records) {
  const ranges = absencesForTechnicianOnDate(technicianId, dateStr, records)
    .filter((row) => recordKind(row) === AVAILABILITY_KIND)
    .map((row) => absenceRangeOnDate(row, dateStr))
    .filter(Boolean);
  return mergeMinuteRanges(ranges);
}

/** Merged unavailable segments for one technician on one day. */
export function unavailabilityRangesOnDate(
  technicianId,
  dateStr,
  records,
  { isJoker = false } = {},
) {
  const inverted = effectiveInvertedJoker(isJoker, dateStr);
  if (inverted) {
    const availableRanges = availabilityRangesOnDate(
      technicianId,
      dateStr,
      records,
    );
    if (!availableRanges.length) {
      return [{ startMin: getDayS(), endMin: getDayE(), reason: '' }];
    }

    const unavailable = [];
    let cursor = getDayS();
    availableRanges.forEach((range) => {
      if (range.startMin > cursor) {
        unavailable.push({
          startMin: cursor,
          endMin: range.startMin,
          reason: '',
        });
      }
      cursor = Math.max(cursor, range.endMin);
    });
    if (cursor < getDayE()) {
      unavailable.push({
        startMin: cursor,
        endMin: getDayE(),
        reason: '',
      });
    }
    return unavailable;
  }

  const ranges = absencesForTechnicianOnDate(technicianId, dateStr, records)
    .filter((row) => recordKind(row) !== AVAILABILITY_KIND)
    .map((row) => absenceRangeOnDate(row, dateStr))
    .filter(Boolean);
  return mergeMinuteRanges(ranges);
}

export function isTechnicianUnavailableOnDate(
  technicianId,
  dateStr,
  records,
  { isJoker = false } = {},
) {
  const inverted = effectiveInvertedJoker(isJoker, dateStr);
  if (inverted) {
    return !availabilityRangesOnDate(technicianId, dateStr, records).length;
  }
  return absencesForTechnicianOnDate(technicianId, dateStr, records).some(
    (row) => recordKind(row) !== AVAILABILITY_KIND,
  );
}

export function isTechnicianFullyUnavailableOnDate(
  technicianId,
  dateStr,
  records,
  { isJoker = false } = {},
) {
  const ranges = unavailabilityRangesOnDate(technicianId, dateStr, records, {
    isJoker,
  });
  // Empty ranges = no unavailable gap (inverted joker: full-day availability).
  if (!ranges.length) return false;
  return ranges.some(
    (range) => range.startMin <= getDayS() && range.endMin >= getDayE(),
  );
}

/** True when the technician is fully unavailable on every date in the list. */
export function isTechnicianFullyUnavailableForWeek(
  technicianId,
  weekDateStrs,
  records,
  { technicians, roles } = {},
) {
  const dates = (weekDateStrs || []).filter(Boolean);
  if (!dates.length) return false;
  const technician = (technicians || []).find(
    (row) => Number(row.id) === Number(technicianId),
  );
  return dates.every((dateStr) => {
    const isJoker = technician
      ? technicianIsJokerOnDate(technician, roles, dateStr)
      : false;
    return isTechnicianFullyUnavailableOnDate(technicianId, dateStr, records, {
      isJoker,
    });
  });
}

/** Count of week days where the technician is fully unavailable. */
export function countFullyUnavailableDaysInWeek(
  technicianId,
  weekDateStrs,
  records,
  { technicians, roles } = {},
) {
  const dates = (weekDateStrs || []).filter(Boolean);
  if (!dates.length) return 0;
  const technician = (technicians || []).find(
    (row) => Number(row.id) === Number(technicianId),
  );
  return dates.reduce((count, dateStr) => {
    const isJoker = technician
      ? technicianIsJokerOnDate(technician, roles, dateStr)
      : false;
    return isTechnicianFullyUnavailableOnDate(technicianId, dateStr, records, {
      isJoker,
    })
      ? count + 1
      : count;
  }, 0);
}

export function isTechnicianUnavailableDuring(
  technicianId,
  dateStr,
  startMin,
  endMin,
  records,
  { isJoker = false } = {},
) {
  if (startMin == null || endMin == null) return false;
  const inverted = effectiveInvertedJoker(isJoker, dateStr);
  if (
    inverted &&
    !availabilityRangesOnDate(technicianId, dateStr, records).length
  ) {
    return true;
  }
  return unavailabilityRangesOnDate(technicianId, dateStr, records, {
    isJoker,
  }).some((range) => range.startMin < endMin && range.endMin > startMin);
}

/** Whether a grid planning slot overlaps a technician unavailability window. */
export function planningSlotOverlapsUnavailability(
  slot,
  records,
  { isJoker = false } = {},
) {
  if (
    !slot?.technicianId ||
    !slot?.date ||
    !slot?.startTime ||
    !slot?.endTime
  ) {
    return false;
  }
  return isTechnicianUnavailableDuring(
    slot.technicianId,
    slot.date,
    t2m(slot.startTime),
    t2m(slot.endTime),
    records,
    { isJoker },
  );
}

export function absenceForTechnicianOnDate(technicianId, dateStr, records) {
  const rows = absencesForTechnicianOnDate(technicianId, dateStr, records);
  return rows[0] || null;
}

/** False when the absence ended before today (hidden from management list, kept on grid). */
export function isUnavailabilityActiveInList(record) {
  if (!record?.endDate) return true;
  return !isPastPlanningDay(record.endDate);
}

export function defaultUnavailabilityKindForTechnician(
  technician,
  roles,
  dateStr = null,
) {
  const ref =
    dateStr ||
    new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });
  if (!technicianIsJokerOnDate(technician, roles, ref)) {
    return UNAVAILABILITY_KIND;
  }
  return jokerUsesInvertedAvailabilityOnDate(ref)
    ? AVAILABILITY_KIND
    : UNAVAILABILITY_KIND;
}

export function technicianRecordsLabel(
  technician,
  roles,
  { plural = false, dateStr = null } = {},
) {
  const isAvailability =
    defaultUnavailabilityKindForTechnician(technician, roles, dateStr) ===
    AVAILABILITY_KIND;
  if (isAvailability) return plural ? 'disponibilités' : 'disponibilité';
  return plural ? 'indisponibilités' : 'indisponibilité';
}

export function unavailabilityHoursLabel(record) {
  if (record?.wholeDay) return 'Journée entière';
  if (!record?.startTime && !record?.endTime) return '';
  return `${record.startTime}–${record.endTime}`;
}

export function planningWorkdayStartTime() {
  return m2t(getDayS());
}

export function planningWorkdayEndTime() {
  return m2t(getDayE());
}
export const BORNAGE_PLANNING_REASON_REASON = 'Bornage planning';

/** Single-day unavailability rows removable from the day-cell context menu. */
export function singleDayUnavailabilityRecordsOnDate(
  technicianId,
  dateStr,
  records,
) {
  return absencesForTechnicianOnDate(technicianId, dateStr, records).filter(
    (row) =>
      recordKind(row) !== AVAILABILITY_KIND &&
      row.startDate === dateStr &&
      row.endDate === dateStr,
  );
}

export function singleDayUnavailabilityRecordsOverlappingOnDate(
  technicianId,
  dateStr,
  records,
  startMin,
  endMin,
) {
  if (startMin == null || endMin == null) return [];
  return singleDayUnavailabilityRecordsOnDate(
    technicianId,
    dateStr,
    records,
  ).filter((row) => {
    const range = absenceRangeOnDate(row, dateStr);
    return Boolean(range && range.startMin < endMin && range.endMin > startMin);
  });
}

export function filterTechniciansForDate(
  technicians,
  dateStr,
  records,
  { roles = [], resolveIsJoker } = {},
) {
  if (!dateStr) return technicians;
  return technicians.filter((tech) => {
    const isJoker = resolveIsJoker
      ? resolveIsJoker(tech, dateStr)
      : technicianIsJokerOnDate(tech, roles, dateStr);
    return !isTechnicianFullyUnavailableOnDate(tech.id, dateStr, records, {
      isJoker,
    });
  });
}
